import { readBeginnerTimeMilliseconds } from '@skyrover/blockly-blocks';
import type { Block } from 'blockly/core';

import { arduinoGenerator, blockTraceComment } from './arduino-generator.js';

type MelodyNote = { freq: number; ms: number };

const NOTE_SEMITONE: Readonly<Record<string, number>> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

function statementPrefix(block: Block, code: string): string {
  return `${blockTraceComment(block)}\n${code}`;
}

function buzzerPin(block: Block): string {
  return String(block.getFieldValue('BUZZER_PIN') ?? '8');
}

function ensureActiveBuzzerSetup(pin: string): void {
  arduinoGenerator.addSetupOnce(`beginner-active-buzzer-${pin}`, `pinMode(${pin}, OUTPUT);`);
}

function ensurePassiveBuzzerSetup(pin: string): void {
  // tone() configures the pin; keep a setup marker for clarity / future use.
  arduinoGenerator.addSetupOnce(`beginner-passive-buzzer-${pin}`, `pinMode(${pin}, OUTPUT);`);
}

/** MIDI-style frequency for note + octave (C4 ≈ 262 Hz). */
function noteFrequency(note: string, octave: number): number {
  const semis = NOTE_SEMITONE[note] ?? 0;
  const midi = (octave + 1) * 12 + semis;
  return Math.max(31, Math.round(440 * 2 ** ((midi - 69) / 12)));
}

function melodyFromNotes(
  notes: Array<{ note: string; octave: number; ms: number }>,
): MelodyNote[] {
  return notes.map(({ note, octave, ms }) => ({
    freq: noteFrequency(note, octave),
    ms,
  }));
}

const MELODIES: Readonly<Record<string, MelodyNote[]>> = {
  HAPPY_BIRTHDAY: melodyFromNotes([
    { note: 'C', octave: 4, ms: 250 },
    { note: 'C', octave: 4, ms: 250 },
    { note: 'D', octave: 4, ms: 500 },
    { note: 'C', octave: 4, ms: 500 },
    { note: 'F', octave: 4, ms: 500 },
    { note: 'E', octave: 4, ms: 1000 },
    { note: 'C', octave: 4, ms: 250 },
    { note: 'C', octave: 4, ms: 250 },
    { note: 'D', octave: 4, ms: 500 },
    { note: 'C', octave: 4, ms: 500 },
    { note: 'G', octave: 4, ms: 500 },
    { note: 'F', octave: 4, ms: 1000 },
  ]),
  TWINKLE_TWINKLE: melodyFromNotes([
    { note: 'C', octave: 4, ms: 400 },
    { note: 'C', octave: 4, ms: 400 },
    { note: 'G', octave: 4, ms: 400 },
    { note: 'G', octave: 4, ms: 400 },
    { note: 'A', octave: 4, ms: 400 },
    { note: 'A', octave: 4, ms: 400 },
    { note: 'G', octave: 4, ms: 800 },
    { note: 'F', octave: 4, ms: 400 },
    { note: 'F', octave: 4, ms: 400 },
    { note: 'E', octave: 4, ms: 400 },
    { note: 'E', octave: 4, ms: 400 },
    { note: 'D', octave: 4, ms: 400 },
    { note: 'D', octave: 4, ms: 400 },
    { note: 'C', octave: 4, ms: 800 },
  ]),
  JINGLE_BELLS: melodyFromNotes([
    { note: 'E', octave: 4, ms: 300 },
    { note: 'E', octave: 4, ms: 300 },
    { note: 'E', octave: 4, ms: 600 },
    { note: 'E', octave: 4, ms: 300 },
    { note: 'E', octave: 4, ms: 300 },
    { note: 'E', octave: 4, ms: 600 },
    { note: 'E', octave: 4, ms: 300 },
    { note: 'G', octave: 4, ms: 300 },
    { note: 'C', octave: 4, ms: 400 },
    { note: 'D', octave: 4, ms: 200 },
    { note: 'E', octave: 4, ms: 800 },
  ]),
  ALARM: [
    { freq: 880, ms: 150 },
    { freq: 0, ms: 100 },
    { freq: 880, ms: 150 },
    { freq: 0, ms: 100 },
    { freq: 880, ms: 150 },
    { freq: 0, ms: 100 },
    { freq: 988, ms: 400 },
    { freq: 0, ms: 200 },
  ],
  DOOR_BELL: [
    { freq: 523, ms: 300 },
    { freq: 392, ms: 500 },
  ],
};

function playMelodyCode(pin: string, melody: MelodyNote[]): string {
  const lines: string[] = [];
  for (const step of melody) {
    if (step.freq <= 0) {
      lines.push(`delay(${step.ms});`);
    } else {
      lines.push(`tone(${pin}, ${step.freq}, ${step.ms});`);
      lines.push(`delay(${step.ms});`);
    }
  }
  lines.push(`noTone(${pin});`);
  lines.push('');
  return lines.join('\n');
}

export function registerBeginnerBuzzerBlockGenerators(): void {
  // --- Active buzzer ---
  arduinoGenerator.forBlock['beginner_active_buzzer_turn'] = (block: Block) => {
    const pin = buzzerPin(block);
    const action = String(block.getFieldValue('ACTION') ?? 'ON');
    ensureActiveBuzzerSetup(pin);
    const level = action === 'OFF' ? 'LOW' : 'HIGH';
    return statementPrefix(block, `digitalWrite(${pin}, ${level});\n`);
  };

  arduinoGenerator.forBlock['beginner_active_buzzer_beep'] = (block: Block) => {
    const pin = buzzerPin(block);
    const durationMs = Math.max(1, readBeginnerTimeMilliseconds(block, 'DURATION'));
    ensureActiveBuzzerSetup(pin);
    return statementPrefix(
      block,
      [
        `digitalWrite(${pin}, HIGH);`,
        `delay(${durationMs});`,
        `digitalWrite(${pin}, LOW);`,
        '',
      ].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_active_buzzer_beep_repeatedly'] = (block: Block) => {
    const pin = buzzerPin(block);
    const count = Math.max(1, Math.round(Number(block.getFieldValue('COUNT') ?? 3)));
    const onMs = Math.max(1, readBeginnerTimeMilliseconds(block, 'ON_TIME'));
    const offMs = Math.max(0, readBeginnerTimeMilliseconds(block, 'OFF_TIME'));
    ensureActiveBuzzerSetup(pin);
    return statementPrefix(
      block,
      [
        `for (int skyroverBeep = 0; skyroverBeep < ${count}; skyroverBeep++) {`,
        `  digitalWrite(${pin}, HIGH);`,
        `  delay(${onMs});`,
        `  digitalWrite(${pin}, LOW);`,
        `  delay(${offMs});`,
        '}',
        '',
      ].join('\n'),
    );
  };

  // --- Passive buzzer ---
  arduinoGenerator.forBlock['beginner_passive_buzzer_play_note'] = (block: Block) => {
    const pin = buzzerPin(block);
    const note = String(block.getFieldValue('NOTE') ?? 'C');
    const octave = Math.min(8, Math.max(1, Number(block.getFieldValue('OCTAVE') ?? 4)));
    const durationMs = Math.max(1, readBeginnerTimeMilliseconds(block, 'DURATION'));
    const freq = noteFrequency(note, octave);
    ensurePassiveBuzzerSetup(pin);
    return statementPrefix(
      block,
      [
        `tone(${pin}, ${freq}, ${durationMs});`,
        `delay(${durationMs});`,
        `noTone(${pin});`,
        '',
      ].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_passive_buzzer_play_melody'] = (block: Block) => {
    const pin = buzzerPin(block);
    const melodyId = String(block.getFieldValue('MELODY') ?? 'HAPPY_BIRTHDAY');
    const melody = MELODIES[melodyId] ?? MELODIES['HAPPY_BIRTHDAY']!;
    ensurePassiveBuzzerSetup(pin);
    return statementPrefix(block, playMelodyCode(pin, melody));
  };

  arduinoGenerator.forBlock['beginner_passive_buzzer_stop'] = (block: Block) => {
    const pin = buzzerPin(block);
    ensurePassiveBuzzerSetup(pin);
    return statementPrefix(block, `noTone(${pin});\n`);
  };
}

registerBeginnerBuzzerBlockGenerators();
